import { Component, input, OnChanges, output, SimpleChanges, ViewChild, signal, computed, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AvatarColors } from '../../core/enums/base.enums';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';
import { UserService } from '../../core/services/user.service';
import { OrganizationService } from '../../core/services/organization.service';
import { AvatarConstants } from '../../core/constants/avatar.constants';
import { IfHasPermissionDirective } from '../../core/directives/if-has-permission/if-has-permission.directive';
import { OrganizationPermission, UserPermission } from '../../core/enums/permissions.enums';
import { DataEntityTypes } from '../../core/enums/data.enums';
import { PermissionDirectiveOptions } from '../../core/models/ui/permission-directive-options.model';

export type AvatarEntityType = 'user' | 'organization';

const ORGANIZATION_AVATAR_DEFAULT_FALLBACK_ICON = 'building';
const ORGANIZATION_AVATAR_DEFAULT_MAX_WIDTH_PX = 200;

// Structural shape, not an API contract, so the `?: T | null` banned on contracts is right here:
// organization and contact identities carry no userId key, and a MemberModel passed straight in
// has it explicitly null.
interface Identity {
  firstName?: string;
  lastName?: string;
  avatarFileId?: string;
  userId?: number | null;
  memberId?: number;
  organizationId?: number;
  organizationName?: string;
  avatarHexColor?: AvatarColors;
}

@Component({
  selector: 'ms-avatar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FontAwesomeModule, ImageCropperComponent, IfHasPermissionDirective],
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss']
})
export class AvatarComponent implements OnChanges {
  // Input signals
  public identity = input<Identity | undefined | null>(null);
  public avatarSize = input<number>(50);
  public avatarShape = input("circle");
  public avatarAdditionalAttributes = input("");
  public isLoading = input(false);
  public isSelected = input(false);
  /**
   * Whether clicking the avatar selects the thing it represents. Defaults to true, which is what every
   * list does. Set it false where the avatar is decoration beside a name and nothing is listening to
   * `clicked` — that suppresses the pointer cursor, which otherwise advertises an interaction that
   * does not exist, and drops the visually-hidden "Select {name}" checkbox, which would otherwise
   * offer a screen reader the same dead control.
   */
  public isSelectable = input(true);
  public showUploadControls = input(false);
  public maxFileSizeInMB = input(AvatarConstants.MAX_IMAGE_SIZE_IN_MB());
  public minDimensions = AvatarConstants.MIN_IMAGE_DIMENSIONS();
  public entityType = input<AvatarEntityType>('user');
  public fallbackIcon = input<string>(ORGANIZATION_AVATAR_DEFAULT_FALLBACK_ICON);
  public imageMaxWidthPx = input<number>(ORGANIZATION_AVATAR_DEFAULT_MAX_WIDTH_PX);

  // Output events
  public clicked = output<any>();
  public avatarChanged = output<Identity>();
  public avatarRemoved = output<void>();

  @ViewChild('fileInput') fileInput: any;

  // Reactive state using signals
  public errorMessage = signal<string>('');
  public showCropper = signal<boolean>(false);
  public selectedFile = signal<File | null>(null);
  public croppedFile = signal<File | null>(null);
  public imageChangedEvent = signal<any>(null);
  public croppedImage = signal<string>('');
  public isUploading = signal<boolean>(false);

  // Computed properties
  public hasError = computed(() => this.errorMessage().length > 0);
  public canApplyCrop = computed(() => this.croppedFile() !== null && !this.isUploading());
  public showUploadBadge = computed(() =>
    this.showUploadControls() && !this.isSelected() && !this.showCropper()
  );
  public initialsFontSize = computed(() => this.calculateFontSize(this.avatarSize()));
  public isOrganization = computed(() => this.entityType() === 'organization');
  public hasOrganizationAvatarImage = computed(() => !!this.identity()?.avatarFileId);
  public uploadPermissionOptions = computed<PermissionDirectiveOptions>(() => {
    if (this.isOrganization()) {
      return {
        requiredPermissions: [OrganizationPermission.Update],
        userPermissionOnly: false,
        // The organization this avatar belongs to, not the viewer's active one — this avatar renders
        // on the organization profile (reachable for any organization the viewer belongs to) and in
        // organization list rows (DEV-2643). DataEntityTypes.Organization has no owner bypass in
        // getIsEntityOwner, so the org-level check is the only thing gating the upload control.
        organizationId: this.identity()?.organizationId,
        dataEntityType: DataEntityTypes.Organization,
        dataEntityId: this.identity()?.organizationId
      };
    }
    return {
      requiredPermissions: [UserPermission.Update],
      userPermissionOnly: true,
      dataEntityType: DataEntityTypes.User,
      // Client-only options bag, where "absent" is undefined rather than the wire's null.
      dataEntityId: this.identity()?.userId ?? undefined
    };
  });

  // Component state
  public colorClass: AvatarColors | undefined;
  public initials: string = "";
  public showInitials: boolean = false;
  public altText: string = "";
  public avatarClass: string = "";

  //Enums
  public UserPermission = UserPermission;
  public OrganizationPermission = OrganizationPermission;
  public DataEntityTypes = DataEntityTypes;

  constructor(
    private userService: UserService,
    private organizationService: OrganizationService
  ) {}

  private initialize(): void {
    this.shouldShowInitials();
    this.initials = AvatarComponent.setInitials(this.identity()?.firstName, this.identity()?.lastName);
    this.avatarClass = AvatarComponent.getAvatarClass(this.avatarSize(), this.avatarShape(), this.avatarAdditionalAttributes());
    this.altText = this.entityType() === 'organization'
      ? this.identity()?.organizationName ?? 'Organization avatar'
      : `${this.identity()?.firstName ?? ''} ${this.identity()?.lastName ?? ''}`.trim();
    this.colorClass = this.identity()?.avatarHexColor;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['identity']) {
      this.initialize();
    }
  }

  private shouldShowInitials(): void {
    if (this.identity && this.identity()?.avatarFileId) {
      this.showInitials = false;
    } else {
      this.showInitials = true;
    }
  }

  public avatarClicked(): void {
    if (!this.showUploadControls() && this.isSelectable()) {
      const id = this.identity()?.memberId || this.identity()?.userId;
      const data = { id: id, checked: !this.isSelected() };
      this.clicked.emit(data);
    }
  }

  public onCheckboxChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const id = this.identity()?.memberId || this.identity()?.userId;
    const data = { id: id, checked: checkbox.checked };
    this.clicked.emit(data);
  }

  public triggerFileInput(): void {
    this.clearError();
    this.fileInput.nativeElement.click();
  }

  public async onFileSelected(event: any): Promise<void> {
    const file = event.target.files[0];
    
    this.resetFileSelection();
    
    if (!file) {
      return;
    }

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select a valid image file.');
      }

      // Validate file size
      const fileSizeInMB = file.size / AvatarConstants.BYTES_IN_MB();
      if (fileSizeInMB > this.maxFileSizeInMB()) {
        throw new Error(`File size must be less than ${this.maxFileSizeInMB()}MB. Your file is ${fileSizeInMB.toFixed(2)}MB.`);
      }

      // Validate dimensions asynchronously
      const dimensions = await AvatarComponent.getImageDimensions(file);

      if (dimensions.height < this.minDimensions || dimensions.width < this.minDimensions) {
        throw new Error(`Image dimensions must be at least ${this.minDimensions}x${this.minDimensions} pixels.`);
      }

      // All validations passed
      this.selectedFile.set(file);
      this.imageChangedEvent.set(event);
      this.showCropper.set(true);
      this.clearError();
      
    } catch (error: any) {
      console.error('Validation error:', error.message);
      this.errorMessage.set(error.message);
      this.clearFileInput();
    }
  }

  public static getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = window.URL.createObjectURL(file);
      img.onload = () => {
        window.URL.revokeObjectURL(img.src);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = (error) => {
        window.URL.revokeObjectURL(img.src);
        reject(error);
      }
    });
  }

  public imageCropped(event: ImageCroppedEvent): void {
    if (event.objectUrl) {
      this.croppedImage.set(event.objectUrl);
    } else if (event.base64) {
      this.croppedImage.set(event.base64);
    }
    
    if (event.blob) {
      this.croppedFile.set(new File([event.blob], 'avatar.png', { type: 'image/png' }));
    }
  }

  public loadImageFailed(): void {
    this.errorMessage.set('Failed to load the selected image. Please try another image.');
    this.showCropper.set(false);
  }

  public async applyCrop(): Promise<void> {
    const croppedFile = this.croppedFile();
    const croppedImage = this.croppedImage();

    if (!croppedImage || !croppedFile) {
      return;
    }

    this.isUploading.set(true);
    this.showCropper.set(false);

    try {
      let updatedAvatarFileId: string | undefined;

      if (this.isOrganization()) {
        const organizationId = this.identity()?.organizationId;
        if (!organizationId) {
          this.errorMessage.set('No organization ID available for upload');
          return;
        }
        const result = await this.organizationService.uploadOrganizationAvatar(organizationId, croppedFile);
        updatedAvatarFileId = result.data?.avatarFileId;
      } else {
        const userId = this.identity()?.userId || this.identity()?.memberId;
        if (!userId) {
          this.errorMessage.set('No user ID available for upload');
          return;
        }
        const result = await this.userService.uploadUserAvatar(userId, croppedFile);
        updatedAvatarFileId = result.data?.avatarFileId;
      }

      if (this.identity() && updatedAvatarFileId) {
        const updatedIdentity: Identity = {
          ...this.identity()!,
          avatarFileId: updatedAvatarFileId
        };
        this.avatarChanged.emit(updatedIdentity);
      }

      this.resetFileSelection();
    } catch (error) {
      console.error('Error uploading avatar:', error);
      this.errorMessage.set('Failed to upload avatar. Please try again.');
    } finally {
      this.isUploading.set(false);
      this.clearFileInput();
    }
  }

  public cancelCrop(): void {
    this.resetFileSelection();
    this.clearFileInput();
  }

  public clearError(): void {
    this.errorMessage.set('');
  }

  private clearFileInput(): void {
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private resetFileSelection(): void {
    this.showCropper.set(false);
    this.croppedImage.set('');
    this.croppedFile.set(null);
    this.imageChangedEvent.set(null);
    this.selectedFile.set(null);
  }

  public static setInitials(firstName: string | undefined, lastName: string | undefined): string {
    return `${firstName?.charAt(0).toUpperCase()}${lastName?.charAt(0).toUpperCase()}`;
  }

  private static getAvatarClass(avatarSize: number, avatarShape: string, avatarAdditionalAttributes: string): string {
    return `symbol symbol-${avatarSize}px symbol-${avatarShape} ${avatarAdditionalAttributes}`;
  }

  private calculateFontSize(avatarSize: number): number {
    const fontSize = Math.round(avatarSize * AvatarConstants.INITIALS_SIZE_FACTOR());
    return fontSize;
  }

  public removeAvatar(): void {
    this.avatarRemoved.emit();
  }
}
